import React from 'react'
import { Flex, theme, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Match, MatchClassification } from '../types'
import { formatAmount, getAmountA, getAmountB, getClassifications } from '../utils/reconciliationHelpers'
import CountUp from '../../../shared/components/CountUp'
import { ClassificationDot, getClassificationColor } from './ClassificationTag'

interface ReconciliationSummaryCardsProps {
  matches: Match[]
  // ISO code of the group's reporting currency (parent company's `currency`).
  groupCurrency: string
}

interface SummaryCardProps {
  label: React.ReactNode
  value: React.ReactNode
  valueColor?: string
  accentColor?: string
}

// One row on wide viewports (every tile is a column, so it never wraps however many
// classifications exist); below TWO_ROW_BREAKPOINT it drops to exactly two rows —
// ceil(count / 2) equal columns — instead of squashing the tiles unreadably thin.
const TWO_ROW_BREAKPOINT = 1200

const CardGrid = styled.div<{ $count: number }>`
  display: grid;
  gap: 16px;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);

  @media (max-width: ${TWO_ROW_BREAKPOINT}px) {
    grid-auto-flow: row;
    grid-template-columns: repeat(${({ $count }) => Math.ceil($count / 2)}, minmax(0, 1fr));
  }
`

const CardRoot = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--sc-padding);
  border-radius: var(--sc-radius);
  background: var(--sc-bg);
  border: 1px solid var(--sc-border);
  transition: border-color var(--sc-motion), box-shadow var(--sc-motion), transform var(--sc-motion);

  &:hover {
    border-color: var(--sc-accent);
    box-shadow: var(--sc-shadow);
    transform: translateY(-2px);
  }
`

const Label = styled.span`
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--sc-label-color);
  line-height: 1.3;
  min-height: ${1.3 * 12 * 2}px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const Value = styled.span`
  margin-top: auto;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--sc-value-color);
`

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, valueColor, accentColor }) => {
  const { token } = theme.useToken()

  return (
    <CardRoot
      style={
        {
          '--sc-radius': `${token.borderRadiusLG}px`,
          '--sc-padding': `${token.padding}px`,
          '--sc-bg': token.colorBgContainer,
          '--sc-border': token.colorBorderSecondary,
          '--sc-accent': accentColor ?? token.colorPrimary,
          '--sc-shadow': token.boxShadowTertiary,
          '--sc-motion': token.motionDurationMid,
          '--sc-label-color': token.colorTextSecondary,
          '--sc-value-color': valueColor ?? token.colorText
        } as React.CSSProperties
      }
    >
      <Label>{label}</Label>
      <Value>{value}</Value>
    </CardRoot>
  )
}

const INVALID_ACCOUNT_CLASSIFICATIONS = [
  MatchClassification.INVALID_INCOME_STATEMENT_ACCOUNT,
  MatchClassification.INVALID_ASSETS_ACCOUNT,
  MatchClassification.INVALID_LIABILITIES_ACCOUNT,
  MatchClassification.INVALID_ACCOUNT
]

const ReconciliationSummaryCards: React.FC<ReconciliationSummaryCardsProps> = ({ matches, groupCurrency }) => {
  const { t } = useTranslation()

  const totalDifference = matches.reduce((sum, match) => sum + Math.abs(getAmountA(match) - getAmountB(match)), 0)

  const countWith = (classification: MatchClassification) =>
    matches.filter(match => getClassifications(match).includes(classification)).length

  const classificationTiles = Object.values(MatchClassification)
    .filter(classification => !INVALID_ACCOUNT_CLASSIFICATIONS.includes(classification))
    .map(classification => ({ classification, count: countWith(classification) }))

  const invalidAccountCount = matches.filter(match =>
    getClassifications(match).some(classification => INVALID_ACCOUNT_CLASSIFICATIONS.includes(classification))
  ).length

  const invalidAccountBreakdown = INVALID_ACCOUNT_CLASSIFICATIONS.map(classification => ({
    classification,
    count: countWith(classification)
  }))

  // classification tiles + "Virheelliset tilit" + "Erot yhteensä"
  const tileCount = classificationTiles.length + 2

  return (
    <CardGrid $count={tileCount}>
      {classificationTiles.map(({ classification, count }) => (
        <SummaryCard
          key={classification}
          label={
            <>
              <ClassificationDot classification={classification} />
              {t(`icReconciliation:classification-${classification}`, classification)}
            </>
          }
          value={<CountUp value={count} />}
          valueColor={getClassificationColor(classification)}
          accentColor={getClassificationColor(classification)}
        />
      ))}
      <Tooltip
        title={
          <Flex vertical gap={4}>
            {invalidAccountBreakdown.map(({ classification, count }) => (
              <Flex key={classification} justify="space-between" gap={16}>
                <span>
                  <ClassificationDot classification={classification} />
                  {t(`icReconciliation:classification-${classification}`, classification)}
                </span>
                <span style={{ fontWeight: 600 }}>{count}</span>
              </Flex>
            ))}
          </Flex>
        }
      >
        <div style={{ height: '100%' }}>
          <SummaryCard
            label={
              <>
                <ClassificationDot classification={MatchClassification.INVALID_INCOME_STATEMENT_ACCOUNT} />
                {t('icReconciliation:summaryInvalidAccounts', 'Virheelliset tilit')}
              </>
            }
            value={<CountUp value={invalidAccountCount} />}
            valueColor={getClassificationColor(MatchClassification.INVALID_INCOME_STATEMENT_ACCOUNT)}
            accentColor={getClassificationColor(MatchClassification.INVALID_INCOME_STATEMENT_ACCOUNT)}
          />
        </div>
      </Tooltip>
      <SummaryCard
        label={`${t('icReconciliation:summaryTotalDifference', 'Erot yhteensä')} (${groupCurrency})`}
        value={<CountUp value={totalDifference} format={animated => formatAmount(animated) || '0,00'} />}
      />
    </CardGrid>
  )
}

export default ReconciliationSummaryCards
